import type { Command } from 'commander';
import { Logger } from '@gitlab-org/logging';
import {
  parsePluginScope,
  parseSpecifier,
  PluginInstaller,
} from '@gitlab-org/duo-plugin-marketplace/node';
import type { DuoCommand, OptionGroup } from '../duo_command';
import { registerOptionsOnCommand } from '../../option_def';
import { ExitHandler } from '../../utils/exit';
import type { PluginContainerProvider } from './plugin_services';
import { describePlugin } from './describe_scope';
import { createScopeOptionDef } from './scope_option_def';

interface InstallOptions {
  scope: string;
}

const scopeOptionDef = createScopeOptionDef('Install scope: user, project, or local.');

export class PluginInstallCommand implements DuoCommand {
  readonly name = 'install <plugin@marketplace>';

  readonly description = 'Install a plugin from a registered marketplace.';

  readonly optionGroups: OptionGroup[] = [
    { title: '`install` options', options: [scopeOptionDef] },
  ];

  readonly #containerProvider: PluginContainerProvider;

  constructor(containerProvider: PluginContainerProvider) {
    this.#containerProvider = containerProvider;
  }

  register(node: Command): void {
    registerOptionsOnCommand(node, [scopeOptionDef]);
    node.action(async (specifier: string, options: InstallOptions) => {
      try {
        const scope = parsePluginScope(options.scope);
        const spec = parseSpecifier(specifier);
        const workspacePath = (node.optsWithGlobals().cwd as string | undefined) ?? process.cwd();
        const installer = this.#containerProvider.getRequiredService(PluginInstaller, node);
        const result = await installer.install(spec, scope, workspacePath);
        // eslint-disable-next-line no-console
        console.log(
          `Installed ${describePlugin(result.id, result.record.version, scope, workspacePath)}`,
        );
      } catch (e) {
        const logger = this.#containerProvider.getRequiredService(Logger, node);
        logger.error('Failed to install plugin', e instanceof Error ? e : new Error(String(e)));
        const message = e instanceof Error ? e.message : String(e);
        // eslint-disable-next-line no-console
        console.error(`Failed to install plugin: ${message}`);
        await this.#containerProvider.getRequiredService(ExitHandler, node).exit(1);
      }
    });
  }
}
